import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  Users,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  FileText,
} from 'lucide-react';

interface ComplianceStats {
  totalUsers: number;
  totalAcknowledgments: number;
  completeAcknowledgments: number;
  incompleteAcknowledgments: number;
  needsReacknowledgment: number;
  parentalConsents: number;
  supabaseUsers: number;
  internalUsers: number;
  complianceRate: number;
  recentAcknowledgments: number;
}

export function ComplianceDashboard() {
  const [stats, setStats] = useState<ComplianceStats>({
    totalUsers: 0,
    totalAcknowledgments: 0,
    completeAcknowledgments: 0,
    incompleteAcknowledgments: 0,
    needsReacknowledgment: 0,
    parentalConsents: 0,
    supabaseUsers: 0,
    internalUsers: 0,
    complianceRate: 0,
    recentAcknowledgments: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComplianceStats();
  }, []);

  const loadComplianceStats = async () => {
    setLoading(true);
    try {
      // Get total users
      const { count: supabaseCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      const { count: internalCount } = await supabase
        .from('internal_users')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      const totalUsers = (supabaseCount || 0) + (internalCount || 0);

      // Get acknowledgments
      const { data: acknowledgments, error } = await supabase
        .from('user_policy_acknowledgments')
        .select('*');

      if (error) throw error;

      const acks = acknowledgments || [];

      // Calculate stats
      const complete = acks.filter(
        (a) =>
          a.privacy_policy_accepted &&
          a.tos_accepted &&
          a.cookies_accepted &&
          a.data_rights_acknowledged &&
          !a.needs_reacknowledgment
      ).length;

      const incomplete = acks.filter(
        (a) =>
          !a.privacy_policy_accepted ||
          !a.tos_accepted ||
          !a.cookies_accepted ||
          !a.data_rights_acknowledged
      ).length;

      const needsReack = acks.filter((a) => a.needs_reacknowledgment).length;
      const parentalConsents = acks.filter((a) => a.parental_consent === true).length;
      const supabaseUsers = acks.filter((a) => a.user_type === 'supabase').length;
      const internalUsers = acks.filter((a) => a.user_type === 'internal').length;

      // Recent acknowledgments (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recent = acks.filter(
        (a) => new Date(a.acknowledged_at) >= sevenDaysAgo
      ).length;

      const complianceRate = totalUsers > 0 ? (complete / totalUsers) * 100 : 0;

      setStats({
        totalUsers,
        totalAcknowledgments: acks.length,
        completeAcknowledgments: complete,
        incompleteAcknowledgments: incomplete,
        needsReacknowledgment: needsReack,
        parentalConsents,
        supabaseUsers,
        internalUsers,
        complianceRate,
        recentAcknowledgments: recent,
      });
    } catch (error) {
      console.error('Error loading compliance stats:', error);
      toast.error('Failed to load compliance statistics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{loading ? '-' : stats.totalUsers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Complete</p>
                <p className="text-2xl font-bold">{loading ? '-' : stats.completeAcknowledgments}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-500/10 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Needs Action</p>
                <p className="text-2xl font-bold">{loading ? '-' : stats.needsReacknowledgment}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recent (7d)</p>
                <p className="text-2xl font-bold">{loading ? '-' : stats.recentAcknowledgments}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Rate */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Overall Compliance Rate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Compliance Progress</span>
              <span className="text-2xl font-bold">{stats.complianceRate.toFixed(1)}%</span>
            </div>
            <Progress value={stats.complianceRate} className="h-3" />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground">Complete Acknowledgments</div>
              <div className="text-lg font-bold text-green-500">{stats.completeAcknowledgments}</div>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground">Incomplete</div>
              <div className="text-lg font-bold text-red-500">{stats.incompleteAcknowledgments}</div>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground">Needs Reacknowledgment</div>
              <div className="text-lg font-bold text-yellow-500">{stats.needsReacknowledgment}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Type Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Supabase</Badge>
                    <span className="text-sm text-muted-foreground">Authenticated Users</span>
                  </div>
                  <span className="font-bold">{stats.supabaseUsers}</span>
                </div>
                <Progress
                  value={
                    stats.totalAcknowledgments > 0
                      ? (stats.supabaseUsers / stats.totalAcknowledgments) * 100
                      : 0
                  }
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Internal</Badge>
                    <span className="text-sm text-muted-foreground">Internal System Users</span>
                  </div>
                  <span className="font-bold">{stats.internalUsers}</span>
                </div>
                <Progress
                  value={
                    stats.totalAcknowledgments > 0
                      ? (stats.internalUsers / stats.totalAcknowledgments) * 100
                      : 0
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              COPPA Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-medium">Parental Consent Records</span>
              </div>
              <p className="text-3xl font-bold">{stats.parentalConsents}</p>
              <p className="text-sm text-muted-foreground mt-2">
                Users under 13 with parental consent
              </p>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Consent Rate</span>
                <span className="font-bold">
                  {stats.parentalConsents > 0 && stats.totalAcknowledgments > 0
                    ? ((stats.parentalConsents / stats.totalAcknowledgments) * 100).toFixed(1)
                    : '0.0'}
                  %
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Compliance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">GDPR Compliance</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Version tracking, consent management, and data rights acknowledgment
              </p>
              <Badge variant="default" className="bg-green-500">Compliant</Badge>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">CCPA Compliance</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Cookie consent and data rights transparency
              </p>
              <Badge variant="default" className="bg-green-500">Compliant</Badge>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">COPPA Compliance</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Parental consent for users under 13
              </p>
              <Badge variant="default" className="bg-green-500">Compliant</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
